import { useCallback } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { z } from 'zod';
import { ExportFormatSchema } from '../../domains/export/models/ExportRequest';
import type { ExportQualityOption } from './useExportJob';
import { useManagedExport } from './useManagedExport';
import { useOrgChartViewStore } from '../../stores/orgchartViewStore';
import { usePerspectiveStore } from '../../stores/perspectiveStore';
import { useI18nLocale } from '../useTranslation';
import { useDiagramInfo } from '../useDiagramInfo';
import { useOrgPlanningScenarioStore } from '../../domains/orgchart/stores/orgPlanningScenarioStore';
import { atlasProjectionMode } from '../../domains/orgchart/services/atlasProjectionContext';
import { useScenariosStore } from '../../core/assistant/stores/scenariosStore';
import { useViewRulesStore } from '../../core/assistant/stores/viewRulesStore';
import { stripBlockedDisplayFields } from '../../core/assistant/models/ViewRules';
import { isSavedPeopleView } from '../../domains/orgchart/models/orgArtifactKind';
import { flushFlowchartTextLayouts } from '../../domains/diagram/shapes/flowchartTextLayoutSync';
import { useDiagramStore } from '../../stores/diagramStore';

type ExportFormat = z.infer<typeof ExportFormatSchema>;

// Process lenses, saved Atlas views, and URL-backed Atlas Insights keep their
// visible rules outside the diagram Y.Doc. Send the sanitized rules the viewer
// sees so export can reproduce that presentation.
const readViewerRulesForExport = () => {
  const { rules, blockedDisplayFields } = useViewRulesStore.getState();
  return stripBlockedDisplayFields(rules, blockedDisplayFields);
};

interface UseDiagramExportOptions {
  diagramId: string | undefined;
  selectedShapeIds?: string[];
}

interface HandleExportPayload {
  format: ExportFormat;
  quality: ExportQualityOption;
  includeBranding?: boolean;
}

export function useDiagramExport({ diagramId, selectedShapeIds }: UseDiagramExportOptions) {
  const { flushPendingUpdates, diagramType } = useDiagramDocument();
  const search = useSearch({ strict: false }) as { lens?: string } | undefined;
  const hasInsightLens = typeof search?.lens === 'string' && search.lens.trim().length > 0;
  const canvasStrokeScale = useDiagramStore((state) => state.stage.scale);
  const diagramInfo = useDiagramInfo(diagramType === 'org' ? diagramId : undefined);
  const selectedScenario = useOrgPlanningScenarioStore((state) => state.selectedByDiagram[diagramId ?? '']);
  const requestedMode = useOrgPlanningScenarioStore((state) => state.modeByDiagram[diagramId ?? '']);
  const projectionMode = atlasProjectionMode(diagramInfo.data?.viewDefinition, Boolean(selectedScenario), requestedMode);
  const isSavedView = isSavedPeopleView(diagramInfo.data?.viewDefinition);
  const hasCanvasDraft = useScenariosStore((state) => {
    const active = state.getActiveScenario();
    return Boolean(active && active.submissionState !== 'submitted');
  });
  const locale = useI18nLocale();
  const perspective = usePerspectiveStore((state) => state.perspective);
  const healthFocusCondition = usePerspectiveStore((state) => state.healthFocusCondition);
  const healthFocusIndexPersonId = usePerspectiveStore((state) => state.healthFocusIndexPersonId);
  const asOfDate = useOrgChartViewStore((state) => state.asOfDate);
  const activeViewpoint = useOrgChartViewStore((state) => state.activeViewpoint);
  const collapsedNodeIds = useOrgChartViewStore((state) => state.collapsedNodeIds);
  const autoLayoutEnabled = useOrgChartViewStore((state) => state.autoLayoutEnabled);
  const showRelationshipCaptions = usePerspectiveStore((state) => state.showRelationshipCaptions);

  const {
    isOpen,
    open,
    close,
    onModalOpenChange,
    startExport,
    handleCancel,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
  } = useManagedExport({
    diagramId,
    selectedShapeIds,
    prepare: flushPendingUpdates,
  });

  const handleConfirm = useCallback(
    async ({ format, quality, includeBranding }: HandleExportPayload) => {
      // Pasted and legacy nodes may not have had a text edit since opening the
      // diagram. Capture their mounted Konva layout before the normal document
      // flush so the exporter receives the same line breaks as the canvas.
      if (diagramType === 'flowchart') {
        await flushFlowchartTextLayouts();
      }
      await startExport({
        format,
        quality,
        locale,
        ...(typeof includeBranding === 'boolean' && { includeBranding }),
        ...(diagramType === 'genogram' ? { showRelationshipCaptions } : {}),
        ...(diagramType === 'flowchart' ? { canvasStrokeScale } : {}),
        ...(diagramType === 'genogram' &&
        (perspective === 'general' || perspective === 'cultural' || perspective === 'health')
          ? { exportViewpoint: perspective }
          : {}),
        ...(diagramType === 'genogram' && perspective === 'health' && healthFocusCondition
          ? { healthFocusCondition, healthFocusIndexPersonId: healthFocusIndexPersonId ?? undefined }
          : {}),
        ...(diagramType === 'flowchart' ? { viewRules: readViewerRulesForExport() } : {}),
        ...(diagramType === 'org'
          ? {
              projectionContext: projectionMode === 'scenario' || hasCanvasDraft
                ? { kind: 'scenario', revision: selectedScenario
                  ? { scenarioId: selectedScenario.scenarioId, changeSetId: selectedScenario.headChangeSetId }
                  : null }
                : { kind: 'canonical' },
              asOfDate: asOfDate ?? undefined,
              activeViewpoint,
              collapsedNodeIds: collapsedNodeIds.length > 0 ? collapsedNodeIds : undefined,
              autoLayoutEnabled,
              ...(isSavedView || hasInsightLens ? { viewRules: readViewerRulesForExport() } : {}),
            }
          : {}),
      });
    },
    [
      activeViewpoint,
      asOfDate,
      autoLayoutEnabled,
      canvasStrokeScale,
      collapsedNodeIds,
      diagramType,
      healthFocusCondition,
      healthFocusIndexPersonId,
      hasCanvasDraft,
      hasInsightLens,
      isSavedView,
      locale,
      perspective,
      projectionMode,
      selectedScenario,
      showRelationshipCaptions,
      startExport,
    ],
  );

  return {
    isOpen,
    open,
    close,
    onModalOpenChange,
    handleConfirm,
    handleCancel,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
  };
}
